/**
 * Scheduled Audit API Service
 * Handles all scheduled audit, auditor invitation, and control review operations
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  config.headers = { ...config.headers, ...getAuthHeaders() };
  return config;
});

// ==================== Scheduled Audits ====================

export const getScheduledAudits = async (params = {}) => {
  const response = await api.get('/api/scheduled-audits', { params });
  return response.data;
};

export const getScheduledAuditById = async (auditId) => {
  const response = await api.get(`/api/scheduled-audits/${auditId}`);
  return response.data;
};

export const createScheduledAudit = async (auditData) => {
  const response = await api.post('/api/scheduled-audits', auditData);
  return response.data;
};

export const updateScheduledAudit = async (auditId, updateData) => {
  const response = await api.put(`/api/scheduled-audits/${auditId}`, updateData);
  return response.data;
};

export const deleteScheduledAudit = async (auditId) => {
  const response = await api.delete(`/api/scheduled-audits/${auditId}`);
  return response.data;
};

export const startAudit = async (auditId) => {
  const response = await api.post(`/api/scheduled-audits/${auditId}/start`);
  return response.data;
};

export const completeAudit = async (auditId) => {
  const response = await api.post(`/api/scheduled-audits/${auditId}/complete`);
  return response.data;
};

// ==================== Auditor Invitations ====================

export const inviteAuditor = async (auditId, inviteData) => {
  const response = await api.post(`/api/scheduled-audits/${auditId}/invite-auditor`, inviteData);
  return response.data;
};

export const getAuditInvitations = async (auditId) => {
  const response = await api.get(`/api/scheduled-audits/${auditId}/invitations`);
  return response.data;
};

export const removeAuditorInvitation = async (auditId, invitationId) => {
  const response = await api.delete(`/api/scheduled-audits/${auditId}/invitations/${invitationId}`);
  return response.data;
};

export const getInvitationByToken = async (token) => {
  const response = await api.get(`/api/scheduled-audits/invitation/${token}`);
  return response.data;
};

export const acceptInvitation = async (token) => {
  const response = await api.post(`/api/scheduled-audits/invitation/${token}/accept`);
  return response.data;
};

// ==================== Control Reviews ====================

export const getAuditControls = async (auditId, params = {}) => {
  const response = await api.get(`/api/scheduled-audits/${auditId}/controls`, { params });
  return response.data;
};

export const getControlDetail = async (auditId, controlId) => {
  const response = await api.get(`/api/scheduled-audits/${auditId}/controls/${controlId}`);
  return response.data;
};

export const updateControlReview = async (auditId, controlId, reviewData) => {
  const response = await api.put(`/api/scheduled-audits/${auditId}/controls/${controlId}/review`, reviewData);
  return response.data;
};

// ==================== Comments ====================

export const addComment = async (auditId, controlId, commentData) => {
  const response = await api.post(`/api/scheduled-audits/${auditId}/controls/${controlId}/comments`, commentData);
  return response.data;
};

export const getComments = async (auditId, controlId) => {
  const response = await api.get(`/api/scheduled-audits/${auditId}/controls/${controlId}/comments`);
  return response.data;
};

export const updateComment = async (auditId, commentId, updateData) => {
  const response = await api.put(`/api/scheduled-audits/${auditId}/comments/${commentId}`, updateData);
  return response.data;
};

// ==================== Summary ====================

export const getAuditSummary = async (auditId) => {
  const response = await api.get(`/api/scheduled-audits/${auditId}/summary`);
  return response.data;
};

// ==================== Available Users ====================

export const getAvailableUsers = async (search = '') => {
  const response = await api.get('/api/scheduled-audits/users/available', { params: { search } });
  return response.data;
};

// ==================== Constants ====================

export const AUDIT_STATUS = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  { value: 'scheduled', label: 'Scheduled', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'under_review', label: 'Under Review', color: 'bg-purple-100 text-purple-700' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' }
];

export const AUDIT_TYPES = [
  { value: 'internal', label: 'Internal Audit (IA)', prefix: 'IA' },
  { value: 'external', label: 'External Audit (EA)', prefix: 'EA' }
];

export const REVIEW_STATUS = [
  { value: 'not_reviewed', label: 'Not Reviewed', color: 'bg-gray-100 text-gray-700' },
  { value: 'in_review', label: 'In Review', color: 'bg-blue-100 text-blue-700' },
  { value: 'reviewed', label: 'Reviewed', color: 'bg-green-100 text-green-700' },
  { value: 'needs_clarification', label: 'Needs Clarification', color: 'bg-orange-100 text-orange-700' },
  { value: 'approved', label: 'Approved', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'issues_found', label: 'Issues Found', color: 'bg-red-100 text-red-700' }
];

export const COMMENT_TYPES = [
  { value: 'comment', label: 'Comment' },
  { value: 'clarification_request', label: 'Clarification Request' },
  { value: 'clarification_response', label: 'Clarification Response' },
  { value: 'observation', label: 'Observation' },
  { value: 'recommendation', label: 'Recommendation' }
];

export const AUDITOR_ROLES = [
  { value: 'lead_auditor', label: 'Lead Auditor' },
  { value: 'auditor', label: 'Auditor' },
  { value: 'observer', label: 'Observer' }
];

// Helper functions
export const getStatusBadge = (status) => {
  const s = AUDIT_STATUS.find(s => s.value === status);
  return s || { value: status, label: status, color: 'bg-gray-100 text-gray-700' };
};

export const getReviewStatusBadge = (status) => {
  const s = REVIEW_STATUS.find(s => s.value === status);
  return s || { value: status, label: status, color: 'bg-gray-100 text-gray-700' };
};
